import React, { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import "../assets/css/pages/BlogDetail.css";
import "../assets/css/Responsive/BlogDetail_Responsive.css";

import addressIcon from "../assets/images/BlogDetail/Address.svg";
import internetIcon from "../assets/images/BlogDetail/internet.svg";
import rightupIcon from "../assets/images/Home/Arrow Right Up.svg";
import { fetchBlogBySlug } from "../api/blog";
import Lenis from "@studio-freight/lenis";
import { useSeo } from "../contexts/SeoContext";
import NotFound from "./NotFound";

// Helper functions
const sanitizeString = (value) => (typeof value === "string" ? value.trim() : "");
const sanitizeImage = (image) => {
    if (!image) return null;
    const url = sanitizeString(image?.url || "");
    if (!url) return null;
    return { url, alt: sanitizeString(image?.alt || "") };
};

// Merges orphaned <a> tags (direct children of the content root, outside any <p>)
// into the preceding or following <p> sibling so they render inline with the sentence.
const mergeOrphanedLinks = (html) => {
    if (typeof window === "undefined" || !html) return html;
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div>${html}</div>`, "text/html");
    const root = doc.querySelector("div");
    if (!root) return html;

    const toRemove = [];
    Array.from(root.childNodes).forEach((node) => {
        if (node.nodeType !== Node.ELEMENT_NODE || node.tagName !== "A") return;
        const prev = node.previousElementSibling;
        if (prev && prev.tagName === "P") {
            prev.appendChild(node.cloneNode(true));
            toRemove.push(node);
        } else {
            const next = node.nextElementSibling;
            if (next && next.tagName === "P") {
                next.insertBefore(node.cloneNode(true), next.firstChild);
                toRemove.push(node);
            }
        }
    });
    toRemove.forEach((n) => n.parentNode && n.parentNode.removeChild(n));
    return root.innerHTML;
};

// Add arrow icon to anchors in HTML content
const addArrowToAnchors = (html, iconSrc) => {
    if (!html || typeof html !== "string") return html;
    // Add arrow icon inside anchors that don't already have one
    return html.replace(
        /<a[^>]*href=["']([^"']+)["'][^>]*>([\s\S]*?)<\/a>/gi,
        (match, href, inner) => {
            // Only add arrow if it's not already there
            if (!inner.includes('<img') && !inner.includes('<svg')) {
                return `<a href="${href}" class="btn" target="_blank" rel="noopener noreferrer">${inner}<img src="${iconSrc}" alt="Arrow icon" style="width: 24px; height: 24px; margin-left: 0.5em;"/></a>`;
            }
            return match;
        }
    );
};

// Helper to check if URL is internal
const isInternalUrl = (url) => {
    if (!url || typeof url !== "string") return false;
    const trimmed = url.trim();
    if (trimmed.startsWith("/")) return true;
    if (trimmed.startsWith("mailto:") || trimmed.startsWith("tel:")) return false;
    try {
        const urlObj = new URL(trimmed, window.location.origin);
        return urlObj.origin === window.location.origin;
    } catch {
        return false;
    }
};

/**
 * Lenis smooth scroll hook (same as other pages)
 */
const useLenis = () => {
    useEffect(() => {
        if (typeof window === "undefined") return;

        document.documentElement.style.scrollBehavior = "auto";

        const lenis = new Lenis({
            duration: 1.2,
            easing: (t) => 1 - Math.pow(1 - t, 3),
            smoothWheel: true,
            smoothTouch: false,
        });

        const raf = (time) => {
            lenis.raf(time);
            requestAnimationFrame(raf);
        };

        requestAnimationFrame(raf);

        return () => {
            lenis.destroy();
        };
    }, []);
};

/**
 * AOS + stagger hook (About-style)
 */
const useAOS = (blogData) => {
    useEffect(() => {
        if (!blogData) {
            return;
        }

        let observer = null;
        const observedElements = [];

        const timer = setTimeout(() => {
            const observerOptions = {
                threshold: 0.1, // Lower threshold
                rootMargin: "0px 0px -50px 0px", // Less negative
            };

            observer = new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    if (!entry.isIntersecting) return;

                    const el = entry.target;
                    if (!el || el.classList.contains("aos-animate")) return;
                    
                    const staggerIndex =
                        parseInt(el.getAttribute("data-stagger-index")) || 0;
                    const delay = staggerIndex * 120;

                    setTimeout(() => {
                        if (el && !el.classList.contains("aos-animate")) {
                            el.classList.add("aos-animate");
                        }
                    }, delay);
                });
            }, observerOptions);

            // Elements we want to animate
            const AOS_SELECTORS = [
                ".blog-cont-1.aos-fade-up",
                ".blog-cont.aos-fade-up",
                ".side-card.aos-fade-up",
                ".aos-fade-up",
            ];

            const elements = document.querySelectorAll(AOS_SELECTORS.join(","));

            if (elements.length === 0) {
                // Fallback: Try again after a longer delay
                setTimeout(() => {
                    const retryElements = document.querySelectorAll(AOS_SELECTORS.join(","));
                    if (retryElements.length > 0) {
                        retryElements.forEach((el) => {
                            if (!el.classList.contains("aos-animate")) {
                                el.classList.add("aos-animate");
                            }
                        });
                    }
                }, 500);
                return;
            }

            elements.forEach((el, index) => {
                if (!el.classList.contains("aos-animate")) {
                    // Check if element is already in viewport (like About page does)
                    const rect = el.getBoundingClientRect();
                    const isInView = rect.top < window.innerHeight * 0.8 && rect.bottom > 0;
                    
                    if (isInView) {
                        // Element is already visible, trigger animation immediately
                        const staggerIndex = parseInt(el.getAttribute("data-stagger-index")) || 0;
                        const delay = staggerIndex * 120;
                        setTimeout(() => {
                            if (el && !el.classList.contains("aos-animate")) {
                                el.classList.add("aos-animate");
                            }
                        }, delay);
                    } else {
                        // Element not in view, observe it
                        observer.observe(el);
                        observedElements.push(el);
                    }
                }
            });
            
            // FALLBACK: Force visibility after 1 second if elements still not animated
            setTimeout(() => {
                elements.forEach((el) => {
                    if (!el.classList.contains("aos-animate")) {
                        el.classList.add("aos-animate");
                    }
                });
            }, 1000);
        }, 200); // Increased delay to ensure DOM is ready

        return () => {
            clearTimeout(timer);
            if (observer) {
                observedElements.forEach((el) => {
                    if (el) observer.unobserve(el);
                });
            }
        };
    }, [blogData]);
};

const getSlugFromPath = (pathname, routeSlug) => {
    if (typeof pathname !== "string" || !pathname) {
        return routeSlug || "";
    }

    // Normalize: remove query/hash and trailing slash (except for root)
    let path = pathname.split("?")[0].split("#")[0];
    if (path.length > 1 && path.endsWith("/")) {
        path = path.slice(0, -1);
    }

    const segments = path.split("/").filter(Boolean);

    // Detect /YYYY/MM/DD/slug pattern
    if (segments.length === 4) {
        const [year, month, day, slugPart] = segments;
        const yearRe = /^\d{4}$/;
        const monthRe = /^(0[1-9]|1[0-2])$/;
        const dayRe = /^(0[1-9]|[12]\d|3[01])$/;

        if (yearRe.test(year) && monthRe.test(month) && dayRe.test(day) && slugPart) {
            return slugPart;
        }
    }

    // Fallback: use slug param from route (e.g. /blog/:slug)
    return routeSlug || "";
};

const isDatePath = (pathname) => {
    if (typeof pathname !== "string" || !pathname) return false;

    let path = pathname.split("?")[0].split("#")[0];
    if (path.length > 1 && path.endsWith("/")) {
        path = path.slice(0, -1);
    }

    const segments = path.split("/").filter(Boolean);
    if (segments.length !== 4) return false;

    const [year, month, day] = segments;
    const yearRe = /^\d{4}$/;
    const monthRe = /^(0[1-9]|1[0-2])$/;
    const dayRe = /^(0[1-9]|[12]\d|3[01])$/;

    return yearRe.test(year) && monthRe.test(month) && dayRe.test(day);
};

export default function Blog_Detail() {
    const params = useParams();
    const routeSlug = params?.slug;
    const navigate = useNavigate();
    const { setPageSeo } = useSeo();

    const [blogData, setBlogData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [notFound, setNotFound] = useState(false);

    const containerRef = useRef(null);
    const sectionRef = useRef(null);

    const [isCloned, setIsCloned] = useState(false);
    const [isRunning, setIsRunning] = useState(false);

    const intervalRef = useRef(null);

    // 🔥 Lenis for smooth scroll
    useLenis();

    // 🔥 AOS + stagger for this page
    useAOS(blogData);

    const [blogSlug, setBlogSlug] = useState("");

    // Resolve slug from URL (supports /blog/:slug and /YYYY/MM/DD/slug)
    useEffect(() => {
        const pathname = window.location.pathname || "";
        const resolvedSlug = getSlugFromPath(pathname, routeSlug);
        setBlogSlug(resolvedSlug);

        // If current path is a date-based URL, redirect to canonical /blog/{slug}
        if (isDatePath(pathname) && resolvedSlug) {
            navigate(`/blog/${resolvedSlug}`, { replace: true });
        }
    }, [routeSlug, navigate]);

    // Fetch blog data
    useEffect(() => {
        if (!blogSlug) {
            setError("Invalid blog slug");
            setLoading(false);
            return;
        }

        const fetchBlog = async () => {
            try {
                setLoading(true);
                setError(null);
                setNotFound(false);
                const data = await fetchBlogBySlug(blogSlug);
                if (data) {
                    setBlogData(data);
                    
                    // Update SEO data
                    if (data.seo) {
                        setPageSeo(data.seo);
                    }
                } else {
                    setError("No data received from API");
                }
            } catch (err) {
                // If the blog doesn't exist, show the NotFound page
                if (err.response && err.response.status === 404) {
                    setNotFound(true);
                } else {
                    setError("Failed to load blog post.");
                }
            } finally {
                setLoading(false);
            }
        };

        fetchBlog();
    }, [blogSlug, setPageSeo]);

    const enableMobileCarousel = () => {
        const c = containerRef.current;
        if (!c || isCloned || isRunning) return;

        // Duplicate items
        c.innerHTML += c.innerHTML;
        setIsCloned(true);

        let speed = 1;

        // Cache scrollWidth to avoid forced reflow in interval
        // Read layout property once before the loop
        const halfScrollWidth = c.scrollWidth / 2;

        intervalRef.current = setInterval(() => {
            c.scrollLeft += speed;
            if (c.scrollLeft >= halfScrollWidth) {
                c.scrollLeft = 0;
            }
        }, 20);

        setIsRunning(true);
    };

    const disableMobileCarousel = () => {
        const c = containerRef.current;
        if (!c) return;

        clearInterval(intervalRef.current);
        setIsRunning(false);

        if (isCloned) {
            const cards = [...c.children];
            const half = cards.length / 2;

            c.innerHTML = ""; // Clear
            for (let i = 0; i < half; i++) {
                c.appendChild(cards[i]);
            }

            setIsCloned(false);
        }

        c.scrollLeft = 0;
    };

    const checkMode = () => {
        if (window.innerWidth <= 1024) {
            // handled by observer now
        } else {
            disableMobileCarousel();
        }
    };

    useEffect(() => {
        checkMode();
        window.addEventListener("resize", checkMode);

        // 🔥 Start/Stop based on visibility for sidebar carousel
        const section = sectionRef.current;
        if (section) {
            const observer = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        if (entry.isIntersecting) {
                            // Visible → start only on mobile width
                            if (window.innerWidth <= 1024) {
                                enableMobileCarousel();
                            }
                        } else {
                            // Out of view → stop
                            disableMobileCarousel();
                        }
                    });
                },
                { threshold: 0.35 } // 35% visible to trigger
            );

            observer.observe(section);

            return () => {
                observer.disconnect();
                clearInterval(intervalRef.current);
                window.removeEventListener("resize", checkMode);
            };
        }

        return () => {
            window.removeEventListener("resize", checkMode);
        };
    }, []);


    // Loading state - show nothing while loading
    if (loading) {
        return <div className="page-loading">Loading blog post...</div>;
    }

    // Show 404 if blog not found
    if (notFound) {
        return <NotFound />;
    }

    // Always render page structure - show content immediately when available
    // If no blogData, still render empty structure (no error blocking)

    // Sanitize data from API response
    const heroImage = blogData ? sanitizeImage(blogData.featured_image) : null;

    // Handle categories - can be array of strings or array of objects with name property
    const categories = Array.isArray(blogData?.categories)
        ? blogData.categories
            .map((cat) => {
                if (typeof cat === "string") return sanitizeString(cat);
                if (cat && typeof cat === "object" && cat.name)
                    return sanitizeString(cat.name);
                return "";
            })
            .filter(Boolean)
        : [];
    const categoriesText = categories.join(", ");

    const title = sanitizeString(blogData?.title || "");
    const content = mergeOrphanedLinks(sanitizeString(blogData?.content || ""));
    const excerpt = sanitizeString(blogData?.excerpt || "");

    // Handle related posts - ensure proper structure from API
    // CRITICAL: Only use related_posts from API, NEVER use recent_blogs
    // related_posts structure: { id, title, slug, excerpt, url, featured_image: {id, url, alt}, date, date_formatted }
    let relatedPosts = [];

    // Explicitly use ONLY blogData.related_posts (NEVER blogData.recent_blogs or blogData.recent_blogs.posts)
    if (blogData && blogData.related_posts && Array.isArray(blogData.related_posts)) {
        // Process only related_posts array - do NOT touch recent_blogs
        relatedPosts = blogData.related_posts
            .filter((post) => {
                // Basic validation: must have id, title, and slug
                return post && post.id && post.title && post.slug;
            })
            .map((post) => {
                // Sanitize all fields
                const title = sanitizeString(post.title || "");
                const slug = sanitizeString(post.slug || "");

                // Double-check: must have both title and slug after sanitization
                if (!title || !slug) return null;

                // Return mapped post with all fields from related_posts
                return {
                    id: post.id,
                    title: title,
                    slug: slug,
                    excerpt: sanitizeString(post.excerpt || ""),
                    url: sanitizeString(post.url || ""),
                    featured_image: sanitizeImage(post.featured_image),
                    date: sanitizeString(post.date || ""),
                    date_formatted: sanitizeString(post.date_formatted || ""),
                };
            })
            .filter((post) => post !== null && post.title && post.slug);
    }

    // Handle recent blogs if available (unused - kept for future use only)
    // NOTE: This is NOT used in the sidebar - only related_posts is used
    const recentBlogs = Array.isArray(blogData?.recent_blogs?.posts)
        ? blogData.recent_blogs.posts.filter((post) => post && (post.title || post.slug))
        : [];

    // Check if content has structured h2.sub-title sections
    const hasStructuredContent = content && /<h2[^>]*class=["']sub-title["'][^>]*>/i.test(content);

    // Parse intro content (content before first h2 with sub-title class)
    let introContent = "";
    if (content && content.trim()) {
        if (hasStructuredContent) {
            const introMatch = content.match(
                /^([\s\S]*?)(?=<h2[^>]*class=["']sub-title["'][^>]*>|$)/
            );
            introContent = introMatch ? introMatch[1].trim() : "";
        } else {
            // If no structured content, use the entire content as intro
            introContent = content.trim();
        }
    }

    // If no intro content found but excerpt exists, use excerpt as fallback
    if (!introContent && excerpt) {
        introContent = excerpt;
    }

    // Parse blog blocks from content (each h2 section)
    // Only parse if content has structured h2.sub-title sections
    const parseBlogBlocks = (html) => {
        if (!html) return [];

        // Only process structured sub-title h2 sections to avoid duplicating intro content
        const hasStructuredSections = /<h2[^>]*class=["']sub-title["'][^>]*>/i.test(html);
        if (!hasStructuredSections) {
            return [];
        }

        const blocks = [];

        const h2Regex =
            /<h2[^>]*class=["']sub-title["'][^>]*>(.*?)<\/h2>([\s\S]*?)(?=<h2[^>]*class=["']sub-title["'][^>]*>|$)/g;
        let match;

        while ((match = h2Regex.exec(html)) !== null) {
            const blockTitle = match[1].trim();
            let blockContent = match[2].trim();

            // Extract address and website from info-row
            const infoRowMatch = blockContent.match(
                /<div[^>]*class=["']info-row["'][^>]*>([\s\S]*?)<\/div>/i
            );
            let address = "";
            let website = "";
            if (infoRowMatch) {
                const infoText = infoRowMatch[1];
                const textParts = infoText
                    .split(/<img[^>]*>/i)
                    .map((t) => t.replace(/<[^>]+>/g, "").trim())
                    .filter(Boolean);
                if (textParts.length > 0) address = textParts[0].trim();
                if (textParts.length > 1) website = textParts[1].trim();
            }

            // Extract ALL content including paragraphs and images
            // This preserves the original structure where images appear after descriptions
            const fullContentMatch = blockContent.match(
                /^([\s\S]*?)(?=<p[^>]*class=["']desc["'][^>]*>|$)([\s\S]*?)$/
            );
            
            // Get all paragraphs and content (including images)
            const allPContent = blockContent.match(
                /<p[^>]*>([\s\S]*?)<\/p>/gi
            );
            
            let descriptions = [];
            let buttonUrl = "";
            let buttonText = "";
            let hasAnchorInParagraph = false;

            if (allPContent) {
                allPContent.forEach((p) => {
                    // Check if this is a desc paragraph
                    if (/<p[^>]*class=["']desc["'][^>]*>/i.test(p)) {
                        const innerMatch = p.match(/<p[^>]*>([\s\S]*?)<\/p>/i);
                        if (innerMatch) {
                            descriptions.push(innerMatch[1].trim());
                        }
                    } else {
                        // Non-desc paragraph: could be an image block, a standalone CTA anchor, or plain text
                        const innerMatch = p.match(/<p[^>]*>([\s\S]*?)<\/p>/i);
                        if (innerMatch) {
                            let content = innerMatch[1].trim();
                            if (content.includes("<img")) {
                                // Image paragraph — add arrow icon only to image-wrapping anchors
                                if (content.includes("<a")) {
                                    content = addArrowToAnchors(content, rightupIcon);
                                }
                                descriptions.push(content);
                            } else if (/^<a[^>]*>[\s\S]*?<\/a>$/.test(content)) {
                                // Paragraph is purely a standalone CTA anchor — render as button
                                hasAnchorInParagraph = true;
                                content = addArrowToAnchors(content, rightupIcon);
                                descriptions.push(content);
                            } else if (content) {
                                // Plain paragraph (possibly with inline links) — push as-is
                                if (content.includes("<a")) {
                                    hasAnchorInParagraph = true;
                                }
                                descriptions.push(content);
                            }
                        }
                    }
                });
            }

            // Only extract standalone button if no anchor was found inside any paragraph
            if (!hasAnchorInParagraph) {
                const buttonLinkMatch = blockContent.match(/<a[^>]*href=["']([^"']+)["'][^>]*>([\s\S]*?)<\/a>/i);
                if (buttonLinkMatch) {
                    buttonUrl = buttonLinkMatch[1].trim();
                    buttonText = buttonLinkMatch[2].replace(/<[^>]+>/g, "").trim();
                }
            }

            if (
                blockTitle ||
                descriptions.length ||
                (buttonText && buttonUrl) ||
                address ||
                website
            ) {
                blocks.push({
                    title: blockTitle,
                    address,
                    website,
                    descriptions,
                    buttonText,
                    buttonUrl,
                });
            }
        }
        return blocks;
    };

    const blogBlocks = parseBlogBlocks(content);

    return (
        <div>
            {heroImage && (
                <div className="canvas" id="blog-detail-canvas">
                    <section className="blog_detail_hero">
                        <div className="blog_detail_hero_image aos-fade-up" data-stagger-index="0">
                            <img
                                src={heroImage.url}
                                alt={
                                    heroImage.alt ||
                                    title ||
                                    "Blog Detail Hero Image"
                                }
                            />
                        </div>
                    </section>
                </div>
            )}
            {/* section 2 */}
            <section className="page-container">
                <div className="canvas">
                    <div className="page-container-inner">
                        {/* LEFT BLOG CONTENT */}
                        <div className="blog-content">
                            {/* INTRO CONTENT */}
                            {(categoriesText || title || introContent) ? (
                                <div
                                    className="blog-cont-1 aos-fade-up"
                                    data-stagger-index={0}
                                >
                                    {categoriesText && (
                                        <p className="category">
                                            {categoriesText}
                                        </p>
                                    )}
                                    {title && (
                                        <h1
                                            className="title"
                                            dangerouslySetInnerHTML={{
                                                __html: title,
                                            }}
                                        />
                                    )}

                                    {introContent && (
                                        <div
                                            className="description"
                                            id="first-desc"
                                            dangerouslySetInnerHTML={{
                                                __html: introContent,
                                            }}
                                        />
                                    )}
                                </div>
                            ) : blogData ? (
                                <div className="blog-cont-1 aos-fade-up" data-stagger-index={0}>
                                    <p>No content available for this blog post.</p>
                                </div>
                            ) : null}

                            {/* BLOG BLOCKS */}
                            {blogBlocks.map((block, index) => {
                                const blockTitle = sanitizeString(block.title);
                                const blockAddress = sanitizeString(
                                    block.address
                                );
                                const blockWebsite = sanitizeString(
                                    block.website
                                );
                                const blockButtonText = sanitizeString(
                                    block.buttonText
                                );
                                const blockButtonUrl = sanitizeString(
                                    block.buttonUrl
                                );

                                if (
                                    !blockTitle &&
                                    block.descriptions.length === 0
                                )
                                    return null;

                                return (
                                    <div
                                        key={`blog-block-${index}`}
                                        className="blog-cont aos-fade-up"
                                        data-stagger-index={index + 1}
                                    >
                                        <div className="sub-blog-content">
                                            {blockTitle && (
                                                <h2
                                                    className="sub-title"
                                                    dangerouslySetInnerHTML={{
                                                        __html: blockTitle,
                                                    }}
                                                />
                                            )}

                                            {(blockAddress ||
                                                blockWebsite) && (
                                                    <div className="info-row">
                                                        {blockAddress && (
                                                            <div className="row-1">
                                                                <img
                                                                    src={
                                                                        addressIcon
                                                                    }
                                                                    alt="Address icon"
                                                                />
                                                                <span>
                                                                    {blockAddress}
                                                                </span>
                                                            </div>
                                                        )}
                                                        {blockWebsite && (
                                                            <div className="row-2">
                                                                <img
                                                                    src={
                                                                        internetIcon
                                                                    }
                                                                    alt="Website icon"
                                                                />
                                                                <span>
                                                                    {blockWebsite}
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}

                                            {block.descriptions.map(
                                                (desc, descIndex) => (
                                                    <p
                                                        key={`desc-${index}-${descIndex}`}
                                                        className="desc"
                                                        id={
                                                            descIndex ===
                                                                block
                                                                    .descriptions
                                                                    .length -
                                                                1
                                                                ? "sec-desc"
                                                                : undefined
                                                        }
                                                        dangerouslySetInnerHTML={{
                                                            __html: desc,
                                                        }}
                                                    />
                                                )
                                            )}

                                            {blockButtonText &&
                                                blockButtonUrl &&
                                                (() => {
                                                    const isInternal =
                                                        isInternalUrl(
                                                            blockButtonUrl
                                                        );
                                                    return isInternal ? (
                                                        <Link
                                                            to={blockButtonUrl}
                                                            className="btn"
                                                        >
                                                            {blockButtonText}
                                                            <img
                                                                src={
                                                                    rightupIcon
                                                                }
                                                                alt="Arrow icon"
                                                            />
                                                        </Link>
                                                    ) : (
                                                        <a
                                                            href={
                                                                blockButtonUrl
                                                            }
                                                            className="btn"
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                        >
                                                            {blockButtonText}
                                                            <img
                                                                src={
                                                                    rightupIcon
                                                                }
                                                                alt="Arrow icon"
                                                            />
                                                        </a>
                                                    );
                                                })()}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* RIGHT SIDEBAR - Only show related_posts, NOT recent_blogs */}
                        {relatedPosts && relatedPosts.length > 0 && (
                            <aside
                                className="sidebar aos-fade-up"
                                ref={sectionRef}
                                data-stagger-index={0}
                            >
                                <h3 className="sidebar-title">
                                    Related Blog
                                </h3>

                                <div
                                    className="sidebar-carousel"
                                    ref={containerRef}
                                >
                                    {relatedPosts.map((post, index) => {
                                        if (!post) return null;

                                        const postImage = post.featured_image;
                                        const postTitle = post.title;
                                        const postSlug = post.slug;

                                        // Validation - skip if missing required fields
                                        if (!postTitle || !postSlug) return null;

                                        return (
                                            <div
                                                key={`related-${post.id || post.slug || index}`}
                                                className="side-card aos-fade-up"
                                                data-stagger-index={index + 1}
                                            >
                                                <Link
                                                    to={`/blog/${postSlug}`}
                                                    className="side-card-link"
                                                >
                                                    {postImage && postImage.url && (
                                                        <div className="blog-img-wrapper">
                                                            <img
                                                                src={postImage.url}
                                                                alt={
                                                                    postImage.alt ||
                                                                    postTitle ||
                                                                    ""
                                                                }
                                                                className="blog-img"
                                                            />
                                                        </div>
                                                    )}
                                                    {postTitle && (
                                                        <p
                                                            className="side-title"
                                                            dangerouslySetInnerHTML={{
                                                                __html: postTitle,
                                                            }}
                                                        />
                                                    )}
                                                </Link>
                                            </div>
                                        );
                                    })}
                                </div>
                            </aside>
                        )}
                    </div>

                    {/* PAGINATION (static) */}
                    <div className="pagination" id="page-wrapper">
                        <svg
                            width="23"
                            height="23"
                            viewBox="0 0 23 23"
                            fill="none"
                        >
                            <path
                                d="M9.30223 7.44141L5.5813 11.1623M5.5813 11.1623L9.30223 14.8833M5.5813 11.1623L16.7441 11.1623"
                                stroke="#777E91"
                                strokeWidth="1.39535"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>

                        <a className="active">1</a>
                        <a>2</a>
                        <a>3</a>
                        <a>4</a>
                        <a>5</a>

                        <svg
                            width="23"
                            height="23"
                            viewBox="0 0 23 23"
                            fill="none"
                        >
                            <path
                                d="M13.0235 14.8842L16.7444 11.1632M16.7444 11.1632L13.0235 7.44232M16.7444 11.1632L5.58159 11.1632"
                                stroke="black"
                                strokeWidth="1.39535"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </div>
                </div>
            </section>
        </div>
    );
}
